import { useEffect, useMemo, useRef, useState } from "react";
import { countries } from "../../global/countriesAndSources";
import { addIgoToUser, deleteIgoFromUser, getIgoFromUser } from "../../HTTP/user";
import {
  fetchIgo,
  fetchIgoById,
  getIgoByThemesId,
  getIgoThemes,
  getIgoByNameAndLanguage,
  getIgoThemesByNameAndLanguage,
} from "../../HTTP/countries";
import Pagination from "../../Helper/Pagination";
import { getStoredAccessibleCountryCodes } from "../../Helper/countryPurchaseAccess";

// Détecte la langue courante du widget Google Translate
const readTranslateLang = () => {
  const m = document.cookie.match(/(?:^|;)\s*googtrans=\/[a-z-]+\/([a-z-]+)/i);
  const fallback = (document.documentElement.lang || navigator.language || "fr").toLowerCase();
  const lang = (m ? m[1] : fallback).split("-")[0];
  return lang;
};

const normalizeThemes = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (resp?.items && Array.isArray(resp.items)) return resp.items;
  return [];
};

export default function Igo() {
  const url = new URL(window.location.href);
  if (url.searchParams.get("page") === null) {
    url.searchParams.set("page", 1);
    window.history.pushState({}, "", url);
  }

  const [searchTerm, setSearchTerm] = useState("");
  // nouvel état : valeur réellement soumise quand on clique sur "Rechercher"
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState("");
  const [igo, setIgo] = useState([]);
  const [nbIgo, setNbIgo] = useState(0);
  const [currentPage, setCurrentPage] = useState(parseInt(url.searchParams.get("page"), 10));
  const [igoPerPage] = useState(20);
  const [igoThemes, setIgoThemes] = useState([]);
  const [selectedIgoTheme, setSelectedIgoTheme] = useState(null);
  const [igoReady, setIgoReady] = useState(false);
  const [igoThemeSearchTerm, setIgoThemeSearchTerm] = useState("");
  const [language, setLanguage] = useState(readTranslateLang());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userIgoIds, setUserIgoIds] = useState(new Set());
  const [followBusy, setFollowBusy] = useState({});
  const [followedIgo, setFollowedIgo] = useState([]);
  const [activeTab, setActiveTab] = useState("global");
  const accessType = localStorage.getItem("premium_access_type");
  const userCountryCode = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null")?.country || null;
    } catch (error) {
      return null;
    }
  }, []);
  const accessibleCodes = useMemo(() => getStoredAccessibleCountryCodes(userCountryCode), [userCountryCode]);

  // anti-course + debounce
  const igoReqId = useRef(0);
  const themeReqId = useRef(0);
  const igoDebounce = useRef(null);
  const themeDebounce = useRef(null);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  // Surveille les changements de langue via le widget
  useEffect(() => {
    const interval = setInterval(() => {
      const lang = readTranslateLang();
      setLanguage((prev) => (prev !== lang ? lang : prev));
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Boot initial: thèmes
  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        const themes = await getIgoThemes();
        setIgoThemes(normalizeThemes(themes));
      } catch (err) {
        console.error(err);
        setError("Erreur de chargement des données.");
        setIgoThemes([]);
      } finally {
        setIgoReady(true);
        setLoading(false);
      }
    };
    boot();
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      setUserId(null);
      setUserIgoIds(new Set());
      return;
    }

    try {
      const parsed = JSON.parse(storedUser);
      setUserId(parsed?.id || null);
    } catch (err) {
      console.error("Invalid user in localStorage", err);
      setUserId(null);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    let alive = true;
    const loadUserIgo = async () => {
      try {
        const res = await getIgoFromUser(userId);
        if (!alive) return;
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.items)
          ? res.items
          : typeof res?.igo_id_list === "string"
          ? res.igo_id_list.split(",").map((id) => id.trim())
          : [];

        const ids = list
          .map((item) => item?.igo_id ?? item?.igoId ?? item?.id ?? item?.igo?.id ?? item)
          .filter(Boolean)
          .map((id) => String(id));

        setUserIgoIds(new Set(ids));

        const details = await Promise.all(
          list.map(async (item) => {
            if (item && typeof item === "object" && (item.label || item.igo?.label)) {
              return item.igo?.label ? item.igo : item;
            }
            const id = item?.igo_id ?? item?.igoId ?? item?.id ?? item?.igo?.id ?? item;
            if (!id) return null;
            try {
              const data = await fetchIgoById(id);
              return Array.isArray(data) ? data[0] : data?.data ?? data;
            } catch (e) {
              return null;
            }
          })
        );
        if (!alive) return;
        setFollowedIgo(details.filter((d) => d && d.label));
      } catch (err) {
        console.error("Erreur lors du chargement des OIG suivies", err);
        setUserIgoIds(new Set());
        setFollowedIgo([]);
      }
    };

    loadUserIgo();
    return () => {
      alive = false;
    };
  }, [userId]);

  // Liste OIG paginée via route dédiée
  useEffect(() => {
    const fetchPaginatedIgo = async () => {
      if (selectedIgoTheme) return;

      try {
        setLoading(true);
        setError(null);
        const res = await fetchIgo(
          (currentPage - 1) * igoPerPage,
          currentPage * igoPerPage,
          submittedSearchTerm.trim()
        );
        setIgo(res?.data || []);
        setNbIgo(res?.count || 0);
      } catch (err) {
        console.error(err);
        setError("Erreur de chargement des Forums Globaux.");
      } finally {
        setIgoReady(true);
        setLoading(false);
      }
    };

    fetchPaginatedIgo();
  }, [currentPage, igoPerPage, submittedSearchTerm, selectedIgoTheme]);

  // Recharger quand le thème change avec pagination
  useEffect(() => {
    const fetchByTheme = async () => {
      if (!selectedIgoTheme) return;
      if (submittedSearchTerm.trim()) return;

      try {
        setLoading(true);
        setError(null);
        const res = await getIgoByThemesId(
          selectedIgoTheme,
          (currentPage - 1) * igoPerPage,
          currentPage * igoPerPage
        );
        setIgo(res?.data || []);
        setNbIgo(res?.count || 0);
      } catch (err) {
        console.error(err);
        setError("Erreur lors du filtrage par thème.");
      } finally {
        setIgoReady(true);
        setLoading(false);
      }
    };
    fetchByTheme();
  }, [selectedIgoTheme, submittedSearchTerm, currentPage, igoPerPage]);

  // Recherche IGO multilingue (debounce) pour mode thème (non paginé)
  useEffect(() => {
    if (!selectedIgoTheme) return;

    if (igoDebounce.current) clearTimeout(igoDebounce.current);

    igoDebounce.current = setTimeout(async () => {
      const q = submittedSearchTerm.trim(); // utilisation de la valeur soumise
      const reqId = ++igoReqId.current;

      if (!q) return;

      try {
        setError(null);

        setLoading(true);
        const lang = language || readTranslateLang();
        const themeId = selectedIgoTheme || null;
        const results = await getIgoByNameAndLanguage(q, lang, themeId);

        if (reqId !== igoReqId.current) return;
        setIgo(results);
        setNbIgo(Array.isArray(results) ? results.length : 0);
      } catch (err) {
        console.error(err);
        if (reqId !== igoReqId.current) return;
        setError("La recherche a échoué. Réessayez.");
      } finally {
        if (reqId === igoReqId.current) setLoading(false);
      }
    }, 300);

    return () => clearTimeout(igoDebounce.current);
  }, [submittedSearchTerm, language, selectedIgoTheme]);

  // Recherche de thèmes multilingue (debounce) — si input vide => tous les thèmes
  useEffect(() => {
    if (themeDebounce.current) clearTimeout(themeDebounce.current);

    themeDebounce.current = setTimeout(async () => {
      const q = igoThemeSearchTerm.trim();
      const reqId = ++themeReqId.current;
      try {
        setError(null);

        if (!q) {
          const all = await getIgoThemes();
          if (reqId !== themeReqId.current) return;
          setIgoThemes(normalizeThemes(all));
          return;
        }

        const lang = language || readTranslateLang();
        const res = await getIgoThemesByNameAndLanguage(q, lang);
        if (reqId !== themeReqId.current) return;
        setIgoThemes(normalizeThemes(res));
      } catch (err) {
        console.error(err);
        if (reqId !== themeReqId.current) return;
        setError("La recherche de thèmes a échoué.");
        setIgoThemes([]);
      }
    }, 250);

    return () => clearTimeout(themeDebounce.current);
  }, [igoThemeSearchTerm, language]);

  // Utilitaire déjà présent
  const parseCountryListIgo = (countryList) => {
    const countriesArray = (countryList || "").split(",").map((c) => c.trim());
    const final = countriesArray
      .map((country) => {
        const countryData = countries.find((c) => c.code === country);
        return countryData ? countryData.name : country;
      })
      .join(", ");
    return final.length > 150 ? final.slice(0, 150) + "..." : final;
  };

  // Post-traitement minimal (on ne filtre plus par texte côté front)
  const filteredIgo = useMemo(() => {
    const baseList = (igo || []).filter((entry) => entry?.label?.trim() && entry?.country_list?.trim());

    if (accessType !== "country_purchase") {
      return baseList;
    }

    const accessible = new Set(accessibleCodes);
    return baseList.filter((entry) => {
      const codes = String(entry?.country_list || "")
        .split(",")
        .map((code) => code.trim().toLowerCase())
        .filter(Boolean);
      return codes.some((code) => accessible.has(code));
    });
  }, [accessType, accessibleCodes, igo]);

  const handleIgo = (igoElement) => {
    sessionStorage.setItem("search_igo", igoElement.name);
    const formattedLink = igoElement.link
      .replace(/^(https?:\/\/)?(www\.)?/, "")
      .replace(/\/$/, "");
    sessionStorage.setItem("search_query", formattedLink + " ");
    sessionStorage.setItem(
      "search_country",
      JSON.stringify(countries.find((c) => c.code === "wd"))
    );
    sessionStorage.setItem("selected_igo", JSON.stringify(igoElement));
    window.location.href = `/igo/${igoElement.id}`;
  };

  const safeThemes = Array.isArray(igoThemes) ? igoThemes : [];
  const isFollowing = (igoId) => userIgoIds.has(String(igoId));

  // déclenche la recherche soumise (bouton ou Enter)
  const submitSearch = () => {
    url.searchParams.set("page", 1);
    window.history.pushState({}, "", url);
    setCurrentPage(1);
    setSubmittedSearchTerm(searchTerm);
  };

  const paginate = (pageNumber) => {
    url.searchParams.set("page", pageNumber);
    window.history.pushState({}, "", url);
    setCurrentPage(pageNumber);
  };

  const toggleFollowIgo = async (igoId, shouldFollow, igoElement) => {
    if (!userId || !igoId) return;
    const normalizedId = String(igoId);

    setFollowBusy((prev) => ({ ...prev, [normalizedId]: true }));
    try {
      const ok = shouldFollow
        ? await addIgoToUser(userId, igoId)
        : await deleteIgoFromUser(userId, igoId);
      if (!ok) return;

      setFollowedIgo((prev) => {
        const without = prev.filter((e) => String(e.id ?? e.igo_id ?? e.igoId) !== normalizedId);
        return shouldFollow && igoElement ? [...without, igoElement] : without;
      });
      setUserIgoIds((prev) => {
        const next = new Set(prev);
        if (shouldFollow) {
          next.add(normalizedId);
        } else {
          next.delete(normalizedId);
        }
        return next;
      });
    } catch (err) {
      console.error("Erreur lors du suivi de l'OIG", err);
    } finally {
      setFollowBusy((prev) => ({ ...prev, [normalizedId]: false }));
    }
  };

  const renderIgoCard = (igoElement, i) => {
    const igoId = igoElement.id ?? igoElement.igo_id ?? igoElement.igoId;
    return (
      <div className="featuresfav" key={igoId ?? i} id={`fav_${i}`}>
        <div className="itemfav rounded-lg shadow m-2" data-id={i}>
          <div className="item-photowrapper">
            <img
              className="item-photofav"
              src={igoElement.image || process.env.PUBLIC_URL + "/favicon.png"}
              alt="article"
            />
          </div>
          <div className="arttfav">
            <div className="item-headerfav" />
            <button onClick={() => handleIgo(igoElement)}>
              <h3 className="item-titlefav">{igoElement.label}</h3>
              <p className="item-textfav">
                <strong className="text-cyan-600">
                  Pays : {parseCountryListIgo(igoElement.country_list || igoElement.country_list_names)}
                </strong>
              </p>
            </button>
            {userId && igoId && (
              <div className="mt-2 flex flex-wrap gap-2">
                {isFollowing(igoId) ? (
                  <button
                    type="button"
                    className="border-2 border-red-500 text-red-600 px-3 py-1 rounded text-sm"
                    disabled={followBusy[String(igoId)]}
                    onClick={() => toggleFollowIgo(igoId, false, igoElement)}
                  >
                    Ne plus suivre
                  </button>
                ) : (
                  <button
                    type="button"
                    className="border-2 border-cyan-600 text-cyan-700 px-3 py-1 rounded text-sm"
                    disabled={followBusy[String(igoId)]}
                    onClick={() => toggleFollowIgo(igoId, true, igoElement)}
                  >
                    Suivre
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const tabClass = (tab) =>
    `px-4 py-2 rounded text-sm font-semibold border-2 ${
      activeTab === tab ? "bg-cyan-600 text-white border-cyan-600" : "border-cyan-600 text-cyan-700"
    }`;

  return (
    <>
      <section className="articlesfav">
        <div className="flex flex-wrap justify-center gap-2 mb-2">
          <button type="button" className={tabClass("global")} onClick={() => setActiveTab("global")}>
            Forums globaux
          </button>
          {userId && (
            <button type="button" className={tabClass("followed")} onClick={() => setActiveTab("followed")}>
              Forums suivis ({followedIgo.length})
            </button>
          )}
        </div>

        {activeTab === "followed" && userId ? (
          <div className="allArticlesDiv h-[60vh] overflow-y-auto" style={{ height: "60vh", overflowY: "auto" }}>
            {followedIgo.length === 0 ? (
              <p className="p-2 text-sm">Vous ne suivez aucun forum pour le moment.</p>
            ) : (
              followedIgo.map(renderIgoCard)
            )}
          </div>
        ) : (
          <>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Rechercher parmi les Forums Globaux..."
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitSearch();
            }}
            className="search-input"
          />
          <button type="button" onClick={submitSearch} className="search-btn">
            Rechercher
          </button>

          <input
            type="text"
            placeholder="Rechercher parmi les thèmes..."
            value={igoThemeSearchTerm}
            onChange={(e) => setIgoThemeSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={selectedIgoTheme || ""}
            onChange={(e) => setSelectedIgoTheme(e.target.value || null)}
            className="search-input"
          >
            <option value="">Tous les thèmes</option>
            {safeThemes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </select>
          <div className="text-xs opacity-70">
            Langue détectée : <strong>{language}</strong>
          </div>
        </div>

        {loading && <div className="p-2 text-sm">Recherche…</div>}
        {error && <div className="p-2 text-sm text-red-600">{error}</div>}

        <div className="allArticlesDiv h-[50vh] overflow-y-auto" style={{ height: "50vh", overflowY: "auto" }}>
          {igoReady && filteredIgo.map(renderIgoCard)}
        </div>
        <Pagination
          itemsPerPage={igoPerPage}
          totalItems={nbIgo}
          paginate={paginate}
          currentPage={currentPage}
        />
          </>
        )}
      </section>
    </>
  );
}
